/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { Fragment } from 'react'
import { jsx, css, type SerializedStyles } from '@emotion/react'

import type { EuiPanelProps } from '@elastic/eui'
import { EuiPanel, EuiSpacer, EuiTitle, EuiText, useEuiTheme } from '@elastic/eui'

import { CuiThemedIcon } from '@modules/cui/ThemedIcon'

import Header from './Header'

import type { ReactNode, FunctionComponent } from 'react'
import type { LandingPageContext } from '@/components/LandingPageContainer/types'

interface Props {
  children?: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  image?: string
  darkImage?: string
  panelProps?: EuiPanelProps
  pageContext?: LandingPageContext
  onPageContextChange?: (type: LandingPageContext) => void
  isLoading?: boolean
  infoMessage?: ReactNode
  hideHeader?: boolean
  imageCssOverride?: SerializedStyles
  style?: React.CSSProperties
}

const useStyles = () => {
  const { euiTheme } = useEuiTheme()

  return {
    container: css({
      width: '100%',
      minHeight: '100%',
      padding: '2rem',
      backgroundColor: euiTheme.colors.lightestShade,
      display: 'flex',
      flexFlow: 'column',
    }),
    content: css({
      // Placeholder for content wrapper
    }),
    panel: css({
      width: 'auto',
      margin: '0 auto',
      maxWidth: '875px',
    }),
    panelNarrow: css({
      maxWidth: '430px',
    }),
    formPanel: css({
      marginTop: euiTheme.size.xl,
      marginBottom: euiTheme.size.xl,
    }),
    formSubmitButton: css({
      '&:disabled': {
        backgroundColor: 'rgba(105, 112, 125, 0.2)',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
    }),
    socialSeparator: css({
      color: euiTheme.colors.subduedText,
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      '&::before, &::after': {
        content: '""',
        flex: 1,
        borderBottom: `1px solid ${euiTheme.colors.lightShade}`,
      },
      '&::before': {
        marginRight: '.75em',
      },
      '&::after': {
        marginLeft: '.75em',
      },
    }),
    formPanelTitle: css({
      fontSize: euiTheme.size.l,
      fontWeight: 600,
      textAlign: 'center',
    }),
  }
}

const LandingPageContainer: FunctionComponent<Props> = ({
  children,
  title,
  subtitle,
  image,
  darkImage,
  isLoading,
  pageContext,
  onPageContextChange,
  panelProps,
  infoMessage,
  hideHeader = false,
  imageCssOverride,
  style,
}) => {
  const styles = useStyles()

  const defaultImageCss = css({
    margin: '0 auto',
    display: 'block',
    width: '200px',
    height: '180px',
  })

  return (
    <div css={styles.container} style={style}>
      {!hideHeader && !isLoading && (
        <Header pageContext={pageContext} onPageContextSwitch={onPageContextChange} />
      )}

      <EuiSpacer size='xl' />

      <div css={styles.content}>
        <EuiPanel
          css={[styles.panel, styles.formPanel, styles.panelNarrow]}
          hasShadow={true}
          {...panelProps}
        >
          {image && (
            <Fragment>
              <CuiThemedIcon
                size='original'
                lightType={image}
                darkType={darkImage ? darkImage : image}
                css={[defaultImageCss, imageCssOverride]}
              />

              <EuiSpacer size='l' />
            </Fragment>
          )}

          {title && (
            <EuiTitle css={styles.formPanelTitle} size='m'>
              <h1>{title}</h1>
            </EuiTitle>
          )}

          {subtitle && (
            <Fragment>
              <EuiSpacer size='m' />

              <EuiText size='m' textAlign='center' color='subdued'>
                {subtitle}
              </EuiText>
            </Fragment>
          )}

          <EuiSpacer size='xl' />

          {children}
        </EuiPanel>
        {infoMessage}
      </div>
    </div>
  )
}

export default LandingPageContainer
